import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  DollarSign, Fuel, TrendingUp, ChevronDown, ChevronUp, ChevronRight,
  Truck as TruckIcon, CheckCircle2, Receipt, ShieldAlert, Gauge,
} from "lucide-react";
import { apiBaseUrl, authHeaders, formatHm } from "./shared";
import LoadingState from "./LoadingState";

// Same mapping FleetDashboard.jsx's Cost Breakdown donut uses -- a truck
// losing money here should look the same shade of "fuel-heavy" or
// "driver-pay-heavy" as the fleet-wide chart, not invent its own palette.
const COST_COLOR = {
  fuel_cost: "#f2994a", toll_cost: "#2563eb", driver_cost: "#1d6fd8",
  insurance_cost: "#e0745d", office_payroll_cost: "#c9a227", maintenance_cost: "#6b7280",
  other_cost: "#8b7fd6",
};
const COST_LABEL = {
  fuel_cost: "Fuel", toll_cost: "Tolls", driver_cost: "Driver pay",
  insurance_cost: "Insurance", office_payroll_cost: "Office/payroll", maintenance_cost: "Maintenance",
  other_cost: "Equipment",
};
const COST_KEYS = Object.keys(COST_LABEL);

// The actual "why" a truck is or isn't profitable, at a glance -- a
// proportional stacked bar instead of making someone subtract 7 columns
// themselves to see fuel is eating this truck's margin.
function CostBreakdownBar({ entity }) {
  const total = entity.total_cost || 0;
  if (!total) return null;
  return (
    <div style={styles.costBar}>
      {COST_KEYS.map((key) => {
        const value = entity[key] || 0;
        if (!value) return null;
        const pct = (value / total) * 100;
        return (
          <div
            key={key}
            style={{ width: `${pct}%`, background: COST_COLOR[key] }}
            title={`${COST_LABEL[key]}: ${fmt0(value)} (${Math.round(pct)}%)`}
          />
        );
      })}
    </div>
  );
}

// Collapses a truck's weeks[] (each already computed server-side, see
// weekly_profit_by_truck/truck_cost_report in fleet_stats.py) into the one
// fact a fleet admin actually wants from a collapsed row: is this truck
// pulling its weight, without opening anything.
function coverageSummary(weeks) {
  if (!weeks || !weeks.length) return null;
  const total = weeks.length;
  const covered = weeks.filter((w) => w.covered).length;
  if (covered === total) return { ok: true, label: total === 1 ? "Covered" : `Covered all ${total} weeks` };
  // "Covered" means profit >= 0 (equipment cost is already netted into
  // each trip's own profit, see fleet_stats.py's truck_cost_report), NOT
  // profit >= weekly_cost_target -- the swing needed to reach "covered"
  // is -profit, not (target - profit). The old formula overstated every
  // shortfall by a full weekly_cost_target.
  const shortfall = weeks.filter((w) => !w.covered).reduce((sum, w) => sum + Math.max(0, -w.profit), 0);
  return {
    ok: false,
    label: total === 1 ? `-${fmt0(shortfall)} to go` : `${covered}/${total} weeks · -${fmt0(shortfall)} to go`,
  };
}

function fmt0(n) {
  return n == null ? "—" : `$${Math.round(n).toLocaleString()}`;
}
function fmtMiles(n) {
  return n == null ? "—" : `${Math.round(n).toLocaleString()} mi`;
}
function isoDate(dtIso) {
  return dtIso ? dtIso.slice(0, 10) : "";
}
// Pure calendar-date string arithmetic -- no Date.toISOString()/UTC
// conversion involved, so this stays correct regardless of the browser's
// own timezone (same approach DispatcherPerformance.jsx uses).
function addDaysIso(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}
// Snaps any picked date back to that same week's Monday -- backend's
// truck_cost_report buckets weeks as plain 7-day windows starting at
// `start` (see fleet_stats.py), so a non-Monday start silently produces
// weeks that don't line up with real calendar weeks. Rather than trust
// every caller to pick a Monday, the picker itself only ever produces one.
function mondayOf(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const day = dt.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}
function fmtDateShort(dtStr) {
  if (!dtStr) return "—";
  const d = new Date(dtStr.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return dtStr.slice(0, 10);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}
function sundayOf(iso) {
  return addDaysIso(mondayOf(iso), 6);
}
function firstOfMonthIso(iso) {
  const [y, m] = iso.split("-").map(Number);
  return `${y}-${String(m).padStart(2, "0")}-01`;
}
// Whole-week-aligned [start, end] (end = inclusive Sunday of the last week)
// for a named quick-range preset. Anchored on the SERVER's current-week
// Monday -- never the browser clock, which can drift a full week from the
// backend in this environment (that's why the page learns "today" from the
// first fetch; see the mount effect). Month presets resolve "which month is
// it" from the Thursday of the current week, so a week straddling a month
// boundary belongs to whichever month owns the bulk of it.
function rangeForPreset(preset, serverMonday) {
  const thursday = addDaysIso(serverMonday, 3);
  if (preset === "last_week") {
    return { start: addDaysIso(serverMonday, -7), end: addDaysIso(serverMonday, -1) };
  }
  if (preset === "last_4_weeks") {
    return { start: addDaysIso(serverMonday, -21), end: addDaysIso(serverMonday, 6) };
  }
  if (preset === "this_month") {
    // Month-to-date, week-aligned: from the Monday of the week containing
    // the 1st, through the current week's Sunday.
    return { start: mondayOf(firstOfMonthIso(thursday)), end: addDaysIso(serverMonday, 6) };
  }
  if (preset === "last_month") {
    const lastDayPrev = addDaysIso(firstOfMonthIso(thursday), -1); // last day of prev month
    return { start: mondayOf(firstOfMonthIso(lastDayPrev)), end: sundayOf(lastDayPrev) };
  }
  return { start: serverMonday, end: addDaysIso(serverMonday, 6) }; // this_week
}

// "Covered" means profit >= 0 -- each trip's own profit already has its
// prorated share of weekly_cost_target netted in (see fleet_stats.py),
// NOT a direct "$X vs $1000" comparison. Old copy said "$163 earned vs
// $1000 target" right next to a green "covered" badge, which read as a
// contradiction even when the math was right. loads_count singles out
// idle weeks (no trips at all) as their own case -- "$0 earned" reads
// very differently when it means "sat idle all week" vs "ran a load that
// happened to net exactly zero."
function WeeklyCoverageBadge({ week }) {
  const label = `${fmtDateShort(week.week_start)} – ${fmtDateShort(addDaysIso(isoDate(week.week_end), -1))}`;
  const idle = !week.loads_count;
  if (week.covered) {
    return (
      <div style={styles.coverageBadgeCovered}>
        <CheckCircle2 size={13} />
        <div>
          <div style={styles.coverageBadgeTitle}>Costs covered -- {label}</div>
          <div style={styles.coverageBadgeSub}>
            {fmt0(week.company_profit)} profit banked after equipment cost (~{fmt0(week.weekly_cost_target)}/wk) and every other real cost
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={styles.coverageBadgeUncovered}>
      <ShieldAlert size={13} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ ...styles.coverageBadgeTitle, color: "#dc2626" }}>{idle ? "No loads" : "Costs not covered"} -- {label}</div>
        <div style={styles.coverageBadgeSub}>
          {idle
            ? `Sat idle all week -- full ${fmt0(week.weekly_cost_target)} equipment cost still outstanding`
            : `${fmt0(-week.profit)} short of covering this week's costs (equipment cost included)`}
        </div>
      </div>
    </div>
  );
}

function TripRow({ trip }) {
  const inTransit = trip.status === "in_transit";
  return (
    <tr>
      <td style={styles.td}>{fmtDateShort(trip.pickup_datetime)}</td>
      <td style={{ ...styles.td, ...styles.tdLane }}>
        {trip.origin || "?"} <ChevronRight size={11} style={{ verticalAlign: -1, opacity: 0.5 }} /> {trip.destination || "?"}
        {inTransit && (
          <span style={styles.inTransitBadge} title="Still in transit -- these are the original simulated plan numbers, not yet reconciled against what actually happens before delivery.">
            In transit
          </span>
        )}
      </td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmtMiles(trip.distance_miles)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.revenue)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.fuel_cost)}</td>
      <td
        style={{ ...styles.td, ...styles.tdNum, ...styles.tdFuelTax }}
        title="State fuel tax already embedded in the Fuel cost to the left -- shown separately for state-by-state reporting, not an additional cost (not part of Total cost or Profit)."
      >
        {fmt0(trip.fuel_tax)}
      </td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.toll_cost)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.driver_cost)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.insurance_cost)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.office_payroll_cost)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.maintenance_cost)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(trip.other_cost)}</td>
      <td style={{ ...styles.td, ...styles.tdNum, fontWeight: 700 }}>{fmt0(trip.total_cost)}</td>
      <td
        style={{ ...styles.td, ...styles.tdNum, fontWeight: 700, color: trip.profit >= 0 ? "var(--brand)" : "#e0745d" }}
        title={trip.wait_cost > 0 ? `Does not include ${fmt0(trip.wait_cost)} in wait time (${formatHm(trip.wait_hours)}), tracked separately` : undefined}
      >
        {trip.profit >= 0 ? "+" : "-"}{fmt0(Math.abs(trip.profit))}
        {trip.wait_cost > 0 && <span style={styles.waitBadge}>excl. {fmt0(trip.wait_cost)} wait</span>}
      </td>
      <td style={{ ...styles.td, ...styles.tdNum, color: trip.profit >= 0 ? "var(--brand)" : "#e0745d" }}>
        {trip.profit_per_mile != null ? `$${trip.profit_per_mile.toFixed(2)}` : "—"}
      </td>
    </tr>
  );
}

const LOADS_PAGE_SIZE = 25;

// Deliberately slim -- Pickup/Truck/Lane/Miles/Revenue/Profit, not the
// full fuel/toll/driver/insurance/etc breakdown TripRow shows. This tab
// exists to let you find/browse a load across the whole fleet quickly;
// the full cost breakdown is one click away by opening that truck in the
// Truck view, not duplicated here.
function LoadRow({ load }) {
  const inTransit = load.status === "in_transit";
  return (
    <tr>
      <td style={styles.td}>{fmtDateShort(load.pickup_datetime)}</td>
      <td style={styles.td}>#{load.unit_number}</td>
      <td style={{ ...styles.td, ...styles.tdLane }}>
        {load.origin || "?"} <ChevronRight size={11} style={{ verticalAlign: -1, opacity: 0.5 }} /> {load.destination || "?"}
        {inTransit && <span style={styles.inTransitBadge}>In transit</span>}
      </td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmtMiles(load.distance_miles)}</td>
      <td style={{ ...styles.td, ...styles.tdNum }}>{fmt0(load.revenue)}</td>
      <td style={{ ...styles.td, ...styles.tdNum, fontWeight: 700, color: load.profit >= 0 ? "var(--brand)" : "#e0745d" }}>
        {load.profit >= 0 ? "+" : "-"}{fmt0(Math.abs(load.profit))}
      </td>
    </tr>
  );
}

function LoadsTable({ loads, page, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(loads.length / LOADS_PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages - 1);
  const pageLoads = loads.slice(clampedPage * LOADS_PAGE_SIZE, (clampedPage + 1) * LOADS_PAGE_SIZE);
  return (
    <div style={styles.loadsCard}>
      <div style={styles.tableWrap}>
        <table style={styles.loadsTable}>
          <thead>
            <tr>
              <th style={styles.th}>Pickup</th>
              <th style={styles.th}>Truck</th>
              <th style={styles.th}>Lane</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Miles</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Revenue</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Profit</th>
            </tr>
          </thead>
          <tbody>
            {pageLoads.map((load) => (
              <LoadRow key={load.load_id} load={load} />
            ))}
            {pageLoads.length === 0 && (
              <tr><td style={styles.td} colSpan={6}>No loads match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {loads.length > 0 && (
        <div style={styles.loadsPager}>
          <span style={styles.loadsPagerLabel}>
            {clampedPage * LOADS_PAGE_SIZE + 1}–{Math.min((clampedPage + 1) * LOADS_PAGE_SIZE, loads.length)} of {loads.length}
          </span>
          <button
            type="button"
            style={styles.loadsPagerBtn}
            onClick={() => onPageChange(clampedPage - 1)}
            disabled={clampedPage === 0}
          >
            &lt; prev
          </button>
          <span style={styles.loadsPagerLabel}>Page {clampedPage + 1} of {totalPages}</span>
          <button
            type="button"
            style={styles.loadsPagerBtn}
            onClick={() => onPageChange(clampedPage + 1)}
            disabled={clampedPage >= totalPages - 1}
          >
            next &gt;
          </button>
        </div>
      )}
    </div>
  );
}

// One small square per week -- a badge-per-week list is fine for a
// handful of weeks but unreadable at 26+ (a 6-month view). Color alone
// tells the story at a glance (green=covered, red=not covered, gray=
// idle/no loads); click a square for that one week's real numbers via
// the same copy WeeklyCoverageBadge already writes, just on demand
// instead of always rendered.
function WeeklyCoverageHeatmap({ weeks }) {
  const [selected, setSelected] = useState(weeks.length <= 8 ? weeks[0]?.week_start : null);
  const selectedWeek = weeks.find((w) => w.week_start === selected) || null;
  return (
    <div>
      <div style={styles.heatmapStrip}>
        {weeks.map((w) => {
          const idle = !w.loads_count;
          const color = idle ? "var(--line)" : w.covered ? "#4cae6d" : "#dc2626";
          const label = `${fmtDateShort(w.week_start)} – ${fmtDateShort(addDaysIso(isoDate(w.week_end), -1))}`;
          return (
            <button
              key={w.week_start}
              type="button"
              onClick={() => setSelected(w.week_start === selected ? null : w.week_start)}
              title={`${label}: ${idle ? "no loads" : w.covered ? `${fmt0(w.company_profit)} profit` : `${fmt0(-w.profit)} short`}`}
              style={{
                ...styles.heatmapCell,
                background: color,
                outline: w.week_start === selected ? "2px solid var(--ink)" : "none",
                outlineOffset: 1,
              }}
            />
          );
        })}
      </div>
      <div style={styles.heatmapLegend}>
        <span style={styles.heatmapLegendItem}><span style={{ ...styles.heatmapLegendDot, background: "#4cae6d" }} /> covered</span>
        <span style={styles.heatmapLegendItem}><span style={{ ...styles.heatmapLegendDot, background: "#dc2626" }} /> not covered</span>
        <span style={styles.heatmapLegendItem}><span style={{ ...styles.heatmapLegendDot, background: "var(--line)" }} /> idle</span>
      </div>
      {selectedWeek && <WeeklyCoverageBadge week={selectedWeek} />}
    </div>
  );
}

function TruckDetailBody({ truck }) {
  return (
    <div style={styles.truckBody}>
      <WeeklyCoverageHeatmap weeks={truck.weeks} />

      {Object.keys(truck.fuel_tax_by_state || {}).length > 0 && (
        <div style={styles.fuelTaxStrip}>
          <Fuel size={12} style={{ color: "var(--muted)" }} />
          <span
            style={{ color: "var(--muted)" }}
            title="Tax embedded in fuel actually purchased in each state -- not a full IFTA net reconciliation, which would also require per-state miles driven to compute refund/owe direction."
          >
            Fuel tax paid by state:
          </span>
          {Object.entries(truck.fuel_tax_by_state)
            .sort((a, b) => b[1] - a[1])
            .map(([state, amount]) => (
              <span key={state} style={styles.fuelTaxChip}>
                {state} {fmt0(amount)}
              </span>
            ))}
        </div>
      )}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Pickup</th>
              <th style={styles.th}>Lane</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Miles</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Revenue</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Fuel</th>
              <th
                style={{ ...styles.th, ...styles.thNum, ...styles.tdFuelTax }}
                title="State fuel tax already embedded in the Fuel cost to the left -- shown separately for state-by-state reporting, not an additional cost (not part of Total cost or Profit)."
              >
                Fuel tax¹
              </th>
              <th style={{ ...styles.th, ...styles.thNum }}>Tolls</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Driver</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Insurance</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Office/payroll</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Maintenance</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Equipment</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Total cost</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Profit</th>
              <th style={{ ...styles.th, ...styles.thNum }}>Profit/mi</th>
            </tr>
          </thead>
          <tbody>
            {truck.trips.map((t) => (
              <TripRow key={t.load_id} trip={t} />
            ))}
          </tbody>
        </table>
        <div style={styles.tableFootnote}>¹ Already included in Fuel -- broken out here for state-by-state reporting only, not an additional cost.</div>
      </div>
    </div>
  );
}

function TruckSection({ truck, open, onToggle }) {
  return (
    <div style={styles.truckCard}>
      <div style={styles.truckHead} onClick={onToggle}>
        <TruckIcon size={14} style={{ color: "var(--muted)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.truckUnit}>Truck #{truck.unit_number}</div>
          <div style={styles.truckSub}>
            {truck.loads_count} trip{truck.loads_count === 1 ? "" : "s"} · {fmtMiles(truck.miles)}
          </div>
        </div>
        <div style={styles.truckStat}>
          <div style={styles.truckStatValue}>{fmt0(truck.revenue)}</div>
          <div style={styles.truckStatLabel}>revenue</div>
        </div>
        <div style={styles.truckStat}>
          <div style={styles.truckStatValue}>{fmt0(truck.total_cost)}</div>
          <div style={styles.truckStatLabel}>cost</div>
        </div>
        <div style={styles.truckStat}>
          <div style={styles.truckStatValue} title="Fixed equipment cost for this truck (per-day, charged whether it hauled or not).">{fmt0(truck.equipment_cost)}</div>
          <div style={styles.truckStatLabel}>equipment</div>
        </div>
        <div style={styles.truckStat}>
          <div style={{ ...styles.truckStatValue, color: truck.profit >= 0 ? "var(--brand)" : "#e0745d" }}>
            {truck.profit >= 0 ? "+" : "-"}{fmt0(Math.abs(truck.profit))}
          </div>
          <div style={styles.truckStatLabel}>
            profit{truck.wait_cost > 0 && ` (excl. ${fmt0(truck.wait_cost)} wait)`}
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: "var(--muted)" }} /> : <ChevronDown size={16} style={{ color: "var(--muted)" }} />}
      </div>

      {open && <TruckDetailBody truck={truck} />}
    </div>
  );
}

// The truck-first view's row -- this IS the story: worst-profit truck at
// the top, dispatcher name kept as a small byline (not the hierarchy),
// coverage answered before you click anything, and a proportional cost
// bar so "why" is visible without doing the subtraction yourself.
function TruckRow({ truck, open, onToggle }) {
  const coverage = coverageSummary(truck.weeks);
  const hasInTransit = truck.trips?.some((t) => t.status === "in_transit");
  return (
    <div style={styles.truckCard}>
      <div style={styles.truckHead} onClick={onToggle}>
        <TruckIcon size={14} style={{ color: "var(--muted)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.truckUnit}>
            Truck #{truck.unit_number} <span style={styles.truckDispatcherByline}>· {truck.dispatcher_name}</span>
            {hasInTransit && (
              <span
                style={styles.inTransitBadge}
                title="At least one trip here is still in transit -- its numbers are the original simulated plan, not yet reconciled against what actually happens before delivery."
              >
                In transit
              </span>
            )}
          </div>
          <div style={styles.truckSub}>
            {truck.loads_count} trip{truck.loads_count === 1 ? "" : "s"} · {fmtMiles(truck.miles)}
          </div>
          <CostBreakdownBar entity={truck} />
        </div>
        {coverage && (
          <span style={{ ...styles.coveragePill, ...(coverage.ok ? styles.coveragePillOk : styles.coveragePillBehind) }}>
            {coverage.ok ? <CheckCircle2 size={11} /> : <ShieldAlert size={11} />} {coverage.label}
          </span>
        )}
        <div style={styles.truckStat}>
          <div style={styles.truckStatValue}>{fmt0(truck.revenue)}</div>
          <div style={styles.truckStatLabel}>revenue</div>
        </div>
        <div style={styles.truckStat}>
          <div style={styles.truckStatValue}>{fmt0(truck.total_cost)}</div>
          <div style={styles.truckStatLabel}>cost</div>
        </div>
        <div style={styles.truckStat}>
          <div style={styles.truckStatValue} title="Fixed equipment cost for this truck (per-day, charged whether it hauled or not).">{fmt0(truck.equipment_cost)}</div>
          <div style={styles.truckStatLabel}>equipment</div>
        </div>
        <div style={styles.truckStat}>
          <div style={{ ...styles.truckStatValue, color: truck.profit >= 0 ? "var(--brand)" : "#e0745d" }}>
            {truck.profit >= 0 ? "+" : "-"}{fmt0(Math.abs(truck.profit))}
          </div>
          <div style={styles.truckStatLabel}>
            profit{truck.wait_cost > 0 && ` (excl. ${fmt0(truck.wait_cost)} wait)`}
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: "var(--muted)" }} /> : <ChevronDown size={16} style={{ color: "var(--muted)" }} />}
      </div>

      {open && <TruckDetailBody truck={truck} />}
    </div>
  );
}

function DispatcherSection({ dispatcher, open, onToggle, openTrucks, onToggleTruck }) {
  return (
    <div style={styles.dispatcherCard}>
      <div style={styles.dispatcherHead} onClick={onToggle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.dispatcherName}>{dispatcher.dispatcher_name}</div>
          <div style={styles.dispatcherSub}>
            {dispatcher.trucks_count} truck{dispatcher.trucks_count === 1 ? "" : "s"} · {dispatcher.loads_count} trip{dispatcher.loads_count === 1 ? "" : "s"}
          </div>
        </div>
        <div style={styles.dispatcherStat}>
          <div style={styles.dispatcherStatValue}>{fmt0(dispatcher.revenue)}</div>
          <div style={styles.dispatcherStatLabel}>revenue</div>
        </div>
        <div style={styles.dispatcherStat}>
          <div style={styles.dispatcherStatValue}>{fmt0(dispatcher.total_cost)}</div>
          <div style={styles.dispatcherStatLabel}>cost</div>
        </div>
        <div style={styles.dispatcherStat}>
          <div style={{ ...styles.dispatcherStatValue, color: dispatcher.profit >= 0 ? "var(--brand)" : "#e0745d" }}>
            {dispatcher.profit >= 0 ? "+" : "-"}{fmt0(Math.abs(dispatcher.profit))}
          </div>
          <div style={styles.dispatcherStatLabel}>profit</div>
        </div>
        <div style={styles.dispatcherStat}>
          <div style={{ ...styles.dispatcherStatValue, color: dispatcher.profit_per_mile == null ? "var(--ink)" : dispatcher.profit_per_mile >= 0 ? "var(--brand)" : "#e0745d" }}>
            {dispatcher.profit_per_mile != null ? `$${dispatcher.profit_per_mile.toFixed(2)}` : "—"}
          </div>
          <div style={styles.dispatcherStatLabel}>profit/mi</div>
        </div>
        {open ? <ChevronUp size={18} style={{ color: "var(--muted)" }} /> : <ChevronDown size={18} style={{ color: "var(--muted)" }} />}
      </div>

      {open && (
        <div style={styles.dispatcherBody}>
          {dispatcher.trucks.map((truck) => (
            <TruckSection
              key={truck.truck_id}
              truck={truck}
              open={openTrucks.has(truck.truck_id)}
              onToggle={() => onToggleTruck(truck.truck_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TruckCostReport() {
  // Quick-range model: a named preset drives a whole-week [rangeStart,
  // rangeEnd] span (both inclusive, Monday..Sunday) that's what actually
  // gets queried. "custom" keeps whatever range is set and reveals a plain
  // From->To picker. serverMonday is the backend's current-week Monday,
  // learned from the first fetch, and anchors every preset (the browser
  // clock is not trusted here -- see rangeForPreset).
  const [serverMonday, setServerMonday] = useState(null);
  const [preset, setPreset] = useState("this_week");
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDispatchers, setOpenDispatchers] = useState(() => new Set());
  const [openTrucks, setOpenTrucks] = useState(() => new Set());
  // "Truck" (default) is the actual story -- every truck, worst profit
  // first, regardless of who dispatches it. "Dispatcher" is the same data
  // re-sliced by ownership for when that hierarchy is what you actually
  // want (mirrors Alerts.jsx's own Truck/Type toggle for the same reason:
  // one page, two legitimate lenses, pick which one answers your question).
  const [groupBy, setGroupBy] = useState("truck");
  const [truckSearch, setTruckSearch] = useState("");
  const isFirstRender = useRef(true);

  // One-time: learn the server's own current week (never trust the
  // browser's clock for "today" -- confirmed in this environment to drift
  // from the backend by as much as a full week) and use it both to render
  // the first fetch and to seed the date-range inputs.
  useEffect(() => {
    axios
      .get(`${apiBaseUrl}/scripted/fleet/truck-cost-report`, authHeaders())
      .then((res) => {
        setData(res.data);
        // The server's own default range is already Monday-aligned (see
        // _default_calendar_week_bounds); learn it as the anchor for every
        // preset and seed the range to "This week".
        const sm = mondayOf(isoDate(res.data.period_start));
        setServerMonday(sm);
        setPreset("this_week");
        const r = rangeForPreset("this_week", sm);
        setRangeStart(r.start);
        setRangeEnd(r.end);
        if (res.data.dispatchers?.[0]) {
          setOpenDispatchers(new Set([res.data.dispatchers[0].dispatcher_id || "unassigned"]));
        }
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch whenever the dispatcher picks a different range -- skipped on
  // mount since the effect above already fetched the default week.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!rangeStart || !rangeEnd) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/scripted/fleet/truck-cost-report`, {
        ...authHeaders(),
        params: { start: rangeStart, end: rangeEnd },
      })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [rangeStart, rangeEnd]);

  // Apply a preset chip. "custom" leaves the current range in place and just
  // reveals the From->To inputs; every other chip recomputes the range off
  // serverMonday, which triggers the refetch effect above.
  function applyPreset(key) {
    setPreset(key);
    if (key === "custom" || !serverMonday) return;
    const r = rangeForPreset(key, serverMonday);
    setRangeStart(r.start);
    setRangeEnd(r.end);
  }

  const dispatchers = data?.dispatchers || [];
  const periodLabel = data
    ? `${fmtDateShort(data.period_start)} – ${fmtDateShort(addDaysIso(isoDate(data.period_end), -1))}`
    : "";
  // Whole weeks the server actually returned (period_end is exclusive -- the
  // day after the last day), so the subtitle's "N weeks" always matches the
  // data on screen rather than whatever the picker last requested.
  const periodWeeks = data
    ? Math.max(1, Math.round((Date.parse(isoDate(data.period_end)) - Date.parse(isoDate(data.period_start))) / (7 * 86400000)))
    : 1;

  // Every truck across every dispatcher, worst profit first -- the actual
  // fleet-wide ranking that used to be impossible to see without manually
  // expanding every dispatcher's accordion looking for it.
  const flatTrucks = useMemo(() => {
    const list = [];
    for (const d of dispatchers) {
      for (const t of d.trucks) {
        list.push({ ...t, dispatcher_id: d.dispatcher_id, dispatcher_name: d.dispatcher_name });
      }
    }
    list.sort((a, b) => a.profit - b.profit);
    return list;
  }, [dispatchers]);

  // Substring match on unit_number, case-insensitive -- "search by truck"
  // meant literally, not a fuzzy fleet-wide search.
  const matchesSearch = (truck) =>
    !truckSearch.trim() || String(truck.unit_number || "").toLowerCase().includes(truckSearch.trim().toLowerCase());

  const filteredFlatTrucks = useMemo(
    () => flatTrucks.filter(matchesSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatTrucks, truckSearch]
  );

  // Fleet-wide totals are summed from the TRUCK rows actually shown (after
  // the truck-search filter), not the raw per-dispatcher aggregates -- so
  // searching "24" collapses the header numbers to just those trucks, the
  // same way the per-dispatcher cards already recompute (filteredDispatchers
  // below). With no search this reduces over every truck and reproduces the
  // old whole-fleet totals exactly (per-truck revenue/cost/equipment/profit/
  // miles/loads sum to each dispatcher's, which summed to the fleet). Note
  // the truck row uses `miles` where the dispatcher row used `total_miles`.
  const fleetTotals = filteredFlatTrucks.reduce(
    (acc, t) => ({
      revenue: acc.revenue + (t.revenue || 0),
      cost: acc.cost + (t.total_cost || 0),
      equipment: acc.equipment + (t.equipment_cost || 0),
      profit: acc.profit + (t.profit || 0),
      miles: acc.miles + (t.miles || 0),
      trucks: acc.trucks + 1,
      loads: acc.loads + (t.loads_count || 0),
    }),
    { revenue: 0, cost: 0, equipment: 0, profit: 0, miles: 0, trucks: 0, loads: 0 }
  );

  // Dispatcher-grouped view: filter each dispatcher's own truck list and
  // recompute that dispatcher's summary numbers from the filtered subset
  // -- showing "3 trucks / $12,000 revenue" above a list that's actually
  // been filtered down to 1 truck would be a real, confusing mismatch.
  // Dispatchers left with zero matching trucks drop out entirely.
  const filteredDispatchers = useMemo(() => {
    if (!truckSearch.trim()) return dispatchers;
    return dispatchers
      .map((d) => {
        const trucks = d.trucks.filter(matchesSearch);
        return {
          ...d,
          trucks,
          trucks_count: trucks.length,
          loads_count: trucks.reduce((s, t) => s + t.loads_count, 0),
          revenue: trucks.reduce((s, t) => s + t.revenue, 0),
          total_cost: trucks.reduce((s, t) => s + t.total_cost, 0),
          equipment_cost: trucks.reduce((s, t) => s + (t.equipment_cost || 0), 0),
          total_miles: trucks.reduce((s, t) => s + (t.miles || 0), 0),
          profit: trucks.reduce((s, t) => s + t.profit, 0),
        };
      })
      .filter((d) => d.trucks.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchers, truckSearch]);

  // Every trip across every truck/dispatcher, flattened into one list for
  // the Loads tab -- most recent pickup first, the natural "what's
  // happened lately" browse order.
  const flatLoads = useMemo(() => {
    const list = [];
    for (const d of dispatchers) {
      for (const t of d.trucks) {
        for (const trip of t.trips) {
          list.push({ ...trip, load_id: trip.load_id, unit_number: t.unit_number, dispatcher_name: d.dispatcher_name });
        }
      }
    }
    list.sort((a, b) => (b.pickup_datetime || "").localeCompare(a.pickup_datetime || ""));
    return list;
  }, [dispatchers]);

  // Broader than matchesSearch (truck view) on purpose -- a flat
  // fleet-wide list is exactly where matching by dispatcher or lane on
  // top of truck # is genuinely useful, not just noise.
  const filteredLoads = useMemo(() => {
    const q = truckSearch.trim().toLowerCase();
    if (!q) return flatLoads;
    return flatLoads.filter(
      (l) =>
        String(l.unit_number || "").toLowerCase().includes(q) ||
        (l.dispatcher_name || "").toLowerCase().includes(q) ||
        (l.origin || "").toLowerCase().includes(q) ||
        (l.destination || "").toLowerCase().includes(q)
    );
  }, [flatLoads, truckSearch]);

  const [loadsPage, setLoadsPage] = useState(0);
  // Any change that reshuffles the result set should land back on page 1
  // -- otherwise switching trucks/search/range can leave you stranded on
  // a now-out-of-range page showing nothing.
  useEffect(() => {
    setLoadsPage(0);
  }, [truckSearch, rangeStart, rangeEnd]);

  const matchCount =
    groupBy === "loads"
      ? filteredLoads.length
      : groupBy === "truck"
      ? filteredFlatTrucks.length
      : filteredDispatchers.reduce((s, d) => s + d.trucks.length, 0);

  const toggleDispatcher = (id) =>
    setOpenDispatchers((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleTruck = (id) =>
    setOpenTrucks((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>COST &amp; PROFIT VISIBILITY</div>
          <h1 style={styles.title}>Truck Economics</h1>
          <div style={styles.subtitle}>
            {data ? `${periodWeeks === 1 ? "Week" : `${periodWeeks} weeks`} of ${periodLabel}` : "Loading…"}
          </div>
        </div>
        <div style={styles.rangePicker}>
          <div style={styles.presetChips}>
            {[
              ["this_week", "This week"],
              ["last_week", "Last week"],
              ["last_4_weeks", "Last 4 weeks"],
              ["this_month", "This month"],
              ["last_month", "Last month"],
              ["custom", "Custom"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                style={{ ...styles.weekPresetBtn, ...(preset === key ? styles.weekPresetBtnActive : {}) }}
              >
                {label}
              </button>
            ))}
          </div>
          {preset === "custom" && rangeStart && rangeEnd && (
            <div style={styles.customRange}>
              <span style={styles.customRangeLabel}>From</span>
              <input
                type="date"
                value={rangeStart}
                // Any picked day snaps back to its own week's Monday -- the
                // report buckets plain 7-day windows, so a non-Monday start
                // would misalign every week (see mondayOf).
                onChange={(e) => e.target.value && setRangeStart(mondayOf(e.target.value))}
                style={styles.dateInput}
              />
              <ChevronRight size={14} style={{ color: "var(--muted)", flexShrink: 0 }} />
              <span style={styles.customRangeLabel}>To</span>
              <input
                type="date"
                value={rangeEnd}
                // Snaps forward to that week's Sunday so the range is always
                // whole weeks no matter which day was clicked.
                onChange={(e) => e.target.value && setRangeEnd(sundayOf(e.target.value))}
                style={styles.dateInput}
              />
            </div>
          )}
        </div>
      </div>

      {!loading && (
        <div style={styles.fleetStrip}>
          <div style={styles.fleetStat}>
            <DollarSign size={14} style={{ color: "var(--muted)" }} />
            <div>
              <div style={styles.fleetStatValue}>{fmt0(fleetTotals.revenue)}</div>
              <div style={styles.fleetStatLabel}>fleet revenue</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <Receipt size={14} style={{ color: "var(--muted)" }} />
            <div>
              <div style={styles.fleetStatValue}>{fmt0(fleetTotals.cost)}</div>
              <div style={styles.fleetStatLabel}>fleet cost</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <Receipt size={14} style={{ color: "#8b7fd6" }} />
            <div>
              <div style={styles.fleetStatValue} title="Fixed equipment cost: every truck x weekly rate, per-day, whether it hauled or not.">
                {fmt0(fleetTotals.equipment)}
              </div>
              <div style={styles.fleetStatLabel}>equipment cost (fixed)</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <TrendingUp size={14} style={{ color: "var(--muted)" }} />
            <div>
              <div style={{ ...styles.fleetStatValue, color: fleetTotals.profit >= 0 ? "var(--brand)" : "#e0745d" }}>{fmt0(fleetTotals.profit)}</div>
              <div style={styles.fleetStatLabel}>fleet profit</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <Gauge size={14} style={{ color: "var(--muted)" }} />
            <div>
              <div style={{ ...styles.fleetStatValue, color: fleetTotals.profit >= 0 ? "var(--brand)" : "#e0745d" }}>
                {fleetTotals.miles ? `$${(fleetTotals.profit / fleetTotals.miles).toFixed(2)}` : "—"}
              </div>
              <div style={styles.fleetStatLabel}>fleet profit/mi</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <TruckIcon size={14} style={{ color: "var(--muted)" }} />
            <div>
              <div style={styles.fleetStatValue}>
                {fleetTotals.trucks} / {fleetTotals.loads}
              </div>
              <div style={styles.fleetStatLabel}>trucks / trips</div>
            </div>
          </div>
        </div>
      )}

      {error && <div style={styles.emptyState}>Couldn't load the cost report: {error}</div>}
      {loading && <LoadingState label="Loading truck cost report…" />}
      {!error && !loading && dispatchers.length === 0 && (
        <div style={styles.emptyState}>No trips with a saved route plan in this range yet.</div>
      )}

      {!error && dispatchers.length > 0 && (
        <div style={styles.groupByRow}>
          <span style={styles.groupByLabel}>Group by</span>
          <div style={styles.groupByToggle}>
            <button
              style={{ ...styles.groupByBtn, ...(groupBy === "truck" ? styles.groupByBtnActive : {}) }}
              onClick={() => setGroupBy("truck")}
            >
              Truck
            </button>
            <button
              style={{ ...styles.groupByBtn, ...(groupBy === "dispatcher" ? styles.groupByBtnActive : {}) }}
              onClick={() => setGroupBy("dispatcher")}
            >
              Dispatcher
            </button>
            <button
              style={{ ...styles.groupByBtn, ...(groupBy === "loads" ? styles.groupByBtnActive : {}) }}
              onClick={() => setGroupBy("loads")}
            >
              Loads
            </button>
          </div>
          <input
            type="text"
            value={truckSearch}
            onChange={(e) => setTruckSearch(e.target.value)}
            placeholder={groupBy === "loads" ? "Search truck #, dispatcher, or lane..." : "Search truck #..."}
            style={styles.truckSearchInput}
          />
          {truckSearch.trim() && (
            <span style={styles.truckSearchCount}>
              {matchCount} match{matchCount === 1 ? "" : "es"}
            </span>
          )}
        </div>
      )}

      {!error && dispatchers.length > 0 && truckSearch.trim() && matchCount === 0 && (
        <div style={styles.emptyState}>Nothing matching "{truckSearch}" in this range.</div>
      )}

      {groupBy === "loads" ? (
        matchCount > 0 && <LoadsTable loads={filteredLoads} page={loadsPage} onPageChange={setLoadsPage} />
      ) : (
        <div style={styles.board}>
          {groupBy === "truck"
            ? filteredFlatTrucks.map((truck) => (
                <TruckRow
                  key={truck.truck_id}
                  truck={truck}
                  open={openTrucks.has(truck.truck_id)}
                  onToggle={() => toggleTruck(truck.truck_id)}
                />
              ))
            : filteredDispatchers.map((d) => (
                <DispatcherSection
                  key={d.dispatcher_id || "unassigned"}
                  dispatcher={d}
                  open={openDispatchers.has(d.dispatcher_id || "unassigned")}
                  onToggle={() => toggleDispatcher(d.dispatcher_id || "unassigned")}
                  openTrucks={openTrucks}
                  onToggleTruck={toggleTruck}
                />
              ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "24px 28px 40px", maxWidth: 1180, margin: "0 auto" },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 20, marginBottom: 20,
  },
  eyebrow: { fontSize: 10.5, fontWeight: 700, letterSpacing: 1.2, color: "var(--brand)", marginBottom: 4 },
  title: { fontSize: 26, fontWeight: 800, color: "var(--ink)", margin: 0, letterSpacing: -0.3 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  rangePicker: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" },
  presetChips: { display: "flex", gap: 4, flexWrap: "wrap" },
  customRange: {
    display: "flex", alignItems: "center", gap: 8, background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 8, padding: "4px 8px",
  },
  customRangeLabel: { fontSize: 11.5, fontWeight: 600, color: "var(--muted)" },
  dateInput: {
    fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 10px",
  },
  weekCountControl: {
    display: "flex", alignItems: "center", gap: 8, background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 8, padding: "3px 6px",
  },
  weekCountBtn: {
    width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, fontWeight: 700, color: "var(--ink)", background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 6, cursor: "pointer", lineHeight: 1,
  },
  weekCountLabel: { fontSize: 12.5, fontWeight: 600, color: "var(--ink)", minWidth: 56, textAlign: "center" },
  weekPresets: { display: "flex", gap: 4 },
  weekPresetBtn: {
    fontSize: 11.5, fontWeight: 600, color: "var(--muted)", background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 7, padding: "6px 9px", cursor: "pointer",
  },
  weekPresetBtnActive: { color: "#fff", background: "var(--brand)", borderColor: "var(--brand)" },

  fleetStrip: {
    display: "flex", gap: 24, marginBottom: 24, padding: "14px 18px",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 12,
  },
  fleetStat: { display: "flex", alignItems: "center", gap: 8 },
  fleetStatValue: { fontSize: 17, fontWeight: 800, color: "var(--ink)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" },
  fleetStatLabel: { fontSize: 10, color: "var(--muted)", marginTop: 1 },

  emptyState: {
    padding: "28px 20px", textAlign: "center", color: "var(--muted)", fontSize: 13,
    background: "var(--card)", border: "1px dashed var(--line)", borderRadius: 12,
  },

  groupByRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
  groupByLabel: { fontSize: 11.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 },
  groupByToggle: {
    display: "flex", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: 2, gap: 2,
  },
  groupByBtn: {
    fontSize: 12, fontWeight: 600, color: "var(--muted)", background: "transparent", border: "none",
    borderRadius: 6, padding: "5px 12px", cursor: "pointer",
  },
  groupByBtnActive: { background: "var(--brand)", color: "#fff" },
  truckSearchInput: {
    fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "6px 10px", width: 150,
  },
  truckSearchCount: { fontSize: 11.5, color: "var(--muted)" },

  board: { display: "flex", flexDirection: "column", gap: 12 },

  dispatcherCard: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" },
  dispatcherHead: { display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", cursor: "pointer" },
  dispatcherName: { fontSize: 16, fontWeight: 800, color: "var(--ink)" },
  dispatcherSub: { fontSize: 12, color: "var(--muted)", marginTop: 2 },
  dispatcherStat: { textAlign: "right", minWidth: 90 },
  dispatcherStatValue: { fontSize: 16, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },
  dispatcherStatLabel: { fontSize: 9.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 1 },
  dispatcherBody: { padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 10 },

  truckCard: { background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden" },
  loadsCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 12,
    padding: 14, boxShadow: "var(--shadow-xs)",
  },
  loadsTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  loadsPager: { display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 12 },
  loadsPagerLabel: { fontSize: 11.5, color: "var(--muted)" },
  loadsPagerBtn: {
    fontSize: 11.5, fontWeight: 600, color: "var(--ink)", background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 7, padding: "5px 10px", cursor: "pointer",
  },
  truckHead: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer" },
  truckUnit: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)" },
  truckSub: { fontSize: 11.5, color: "var(--muted)", marginTop: 1 },
  truckStat: { textAlign: "right", minWidth: 76 },
  truckStatValue: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },
  truckStatLabel: { fontSize: 9, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3 },
  truckBody: { padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 8 },
  heatmapStrip: { display: "flex", flexWrap: "wrap", gap: 3 },
  heatmapCell: { width: 14, height: 14, border: "none", borderRadius: 3, padding: 0, cursor: "pointer" },
  heatmapLegend: { display: "flex", gap: 12, marginTop: 6, marginBottom: 2 },
  heatmapLegendItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--muted)" },
  heatmapLegendDot: { width: 8, height: 8, borderRadius: 2, display: "inline-block" },
  truckDispatcherByline: { fontWeight: 500, color: "var(--muted)", fontSize: 11.5 },
  inTransitBadge: {
    display: "inline-block", marginLeft: 8, fontSize: 9.5, fontWeight: 700, color: "#2563eb",
    background: "rgba(37,99,235,0.1)", borderRadius: 999, padding: "1px 7px", verticalAlign: "middle",
  },

  costBar: {
    display: "flex", height: 5, borderRadius: 999, overflow: "hidden", marginTop: 7,
    background: "var(--line)", maxWidth: 280,
  },

  coveragePill: {
    display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 700,
    padding: "3px 9px", borderRadius: 999, flexShrink: 0, whiteSpace: "nowrap",
  },
  coveragePillOk: { color: "#1f7a4d", background: "rgba(76,174,109,0.14)" },
  coveragePillBehind: { color: "#e0745d", background: "rgba(224,116,93,0.12)" },

  coverageBadgeCovered: {
    display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px",
    fontSize: 11.5, color: "#1f7a4d", background: "rgba(76,174,109,0.14)",
    border: "1px solid rgba(76,174,109,0.35)", borderRadius: 8,
  },
  coverageBadgeUncovered: {
    display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px",
    fontSize: 11.5, color: "var(--muted)", background: "rgba(220,38,38,0.08)",
    border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8,
  },
  coverageBadgeTitle: { fontWeight: 700 },
  coverageBadgeSub: { marginTop: 1, opacity: 0.9 },

  fuelTaxStrip: {
    display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, fontSize: 11,
    padding: "6px 2px",
  },
  fuelTaxChip: {
    fontSize: 10.5, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 999, padding: "2px 8px",
  },

  tableWrap: { overflowX: "auto" },
  tableFootnote: { fontSize: 10.5, color: "var(--muted)", marginTop: 6, fontStyle: "italic" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 11.5, minWidth: 1060 },
  waitBadge: {
    display: "inline-block", marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: "#a16207",
    background: "rgba(161,98,7,0.12)", borderRadius: 999, padding: "1px 6px", verticalAlign: "middle",
  },
  th: {
    textAlign: "left", padding: "6px 8px", color: "var(--muted)", fontWeight: 600,
    borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
  },
  thNum: { textAlign: "right" },
  td: { padding: "6px 8px", color: "var(--ink-2)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  tdNum: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  tdLane: { whiteSpace: "normal", minWidth: 160 },
  // Deliberately NOT styled like the summed cost columns either side of it
  // -- it's a breakdown of Fuel (tax already embedded in that dollar
  // figure), not an additional cost, so it shouldn't look like one. Muted,
  // smaller, and set off with its own faint background rather than just
  // sitting in the flow of columns that add up into Total cost.
  tdFuelTax: {
    color: "var(--muted)", fontSize: "0.9em", fontStyle: "italic",
    background: "var(--bg)", borderLeft: "1px dashed var(--line)", borderRight: "1px dashed var(--line)",
  },
};
